import { QueryClient } from '@tanstack/react-query';

import { Database } from '~/types/database.types';
import JobState from '~/types/JobState.types';
import { CheckStatus, CheckType, EvidenceType } from '~/types/ProcessCheck.types';
import { getAuthToken } from '~/utils/auth';
import { supabase } from '~/utils/supabaseClient';

export type QualityCheck = {
  job_card_id: string;
  job_card_current_status: JobState;
  job: string;
  in_process_check: {
    check?: string;
    content?: string;
    name: string;
    order: number;
    type: CheckType;
    images: { path: string; title: string }[];
    evidence_files:
      | {
          path: string;
          title: string;
          bucket: string;
        }[]
      | null;
    check_status?: CheckStatus | null;
    addressed_on?: string | null;
    required_evidence: EvidenceType[];
    measurement_unit?: string;
    evidence_measurement?: number;
    threshold?: {
      lower_threshold: number;
      upper_threshold: number;
    };
    expiry_date: string;
  };
  signer?: string;
  work_order_id: string;
  work_order_name: string;
  bill_of_operations_name: string;
  station?: string;
  project?: string;
};

export const jobQualityCheckQuery = (jobCardId?: string, orderNumber?: number) => ({
  queryKey: ['quality', 'check', jobCardId, orderNumber],
  queryFn: async () => {
    const { data } = await supabase
      .rpc('get_in_process_check_details', {
        pin_job_card_id: jobCardId!,
        pin_check_order: orderNumber!,
      })
      .single();
    return data as QualityCheck | null;
  },
});

export type CheckTimelineEvent = Database['public']['Tables']['job_events']['Row'] & {
  job_cards: {
    job_card_id: string;
    workers: {
      worker_first_name: string;
      worker_last_name: string;
    };
  } | null;
  job_event_profile:
    | {
        id: string;
        profile_first_name: string | null;
        profile_last_name: string | null;
      }
    | undefined;
};

export const checkTimelineQuery = (jobCardId: string, checkOrderNumber: number) => ({
  queryKey: ['quality', 'check', 'timeline', jobCardId, checkOrderNumber],
  queryFn: async () => {
    const { data } = await supabase
      .from('job_events')
      .select(
        `
      *,
      job_cards!inner(
        job_card_id,
        workers!job_cards_worker_id_fkey(
          worker_first_name,
          worker_last_name
        )
      )
    `,
      )
      .eq('job_card_id', jobCardId)
      .eq('job_event_type', 'in_process_check_changed')
      .order('job_event_occurrence', { ascending: false })
      .eq('job_event_metadata->in_process_check->order', checkOrderNumber);
    return data as CheckTimelineEvent[] | null;
  },
});

export type CheckStatusMutationParams = {
  jobCardId: string;
  orderNumber: number;
  checkStatus: CheckStatus;
};
export const loader =
  (queryClient: QueryClient) =>
  async ({ params }: { params: { jobCardId: string; orderNumber?: string } }) => {
    const parsed = params.orderNumber !== undefined ? parseInt(params.orderNumber, 10) : NaN;
    const orderNumber = Number.isFinite(parsed) ? parsed : undefined;
    const query = jobQualityCheckQuery(params.jobCardId, orderNumber);
    return (
      queryClient.getQueryData<QualityCheck | null>(query.queryKey) ??
      (await queryClient.fetchQuery(query))
    );
  };

export interface SubmitCheckParams {
  job_card_id: string;
  in_process_check_order_number: number;
  check_status: string;
  evidence_files?: { path: string; title: string }[];
  evidence_note?: string;
  time_to_correct?: string;
  partner_name: string;
}

export const submitCheckMutation = async (params: SubmitCheckParams) => {
  const { partner_name, ...body } = params;
  const token = getAuthToken();
  if (!token) {
    throw new Error('No authentication token available');
  }

  const response = await fetch(
    `${import.meta.env.VITE_TRUNK_BACKEND_URL}/quality/evidence/submit-check`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        authorization: `Bearer ${token}`,
        partner_name,
      },
      body: JSON.stringify(body),
    },
  );
  if (!response.ok) {
    throw new Error('Failed to submit check');
  }
  return response.json();
};
